import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, ExternalLink, Github, FileText } from "lucide-react";
import MarkdownLite from "../components/MarkdownLite";
import HashTag from "../components/HashTag";
import NoteCard from "../components/NoteCard";
import { getBlock, getNote, notes } from "../data/notes";

const fade = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

function Lightbox({ src, alt, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <img
        src={src}
        alt={alt}
        className="max-w-full max-h-[90vh] rounded-lg shadow-2xl border border-border"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

export default function NotePage() {
  const { blockSlug, noteSlug } = useParams();
  const note = getNote(blockSlug, noteSlug);
  const block = getBlock(blockSlug);
  const [lightbox, setLightbox] = useState(null);

  if (!note) {
    return (
      <div className="py-20 px-4 text-center">
        <p className="text-muted mb-4">That note doesn’t exist.</p>
        <Link to="/projects" className="text-accent hover:text-accent-hover">
          ← Back to Projects
        </Link>
      </div>
    );
  }

  const related = notes
    .filter((n) => n.slug !== note.slug && n.tags?.some((t) => note.tags?.includes(t)))
    .slice(0, 3);

  const links = note.links || {};

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
      className="py-10 md:py-14 px-4 md:px-8"
    >
      <Helmet>
        <title>{note.title} | Soham Desai</title>
        <meta name="description" content={note.summary} />
      </Helmet>

      <article className="max-w-3xl mx-auto">
        {/* Breadcrumb */}
        <motion.div variants={fade} className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
          <Link to="/projects" className="hover:text-foreground transition-colors">
            Projects
          </Link>
          <ChevronLeft className="w-3.5 h-3.5 rotate-180" />
          <Link to={`/projects/${blockSlug}`} className="hover:text-foreground transition-colors">
            {block?.title || blockSlug}
          </Link>
        </motion.div>

        {/* Header */}
        <motion.div variants={fade} className="mb-4 flex flex-wrap items-center gap-2">
          {note.isNew && (
            <span className="px-2.5 py-0.5 rounded-full bg-accent/10 text-accent text-[0.7rem] tracking-wide uppercase border border-accent/20">
              New
            </span>
          )}
          {block && (
            <Link
              to={`/projects/${blockSlug}`}
              className="px-2.5 py-0.5 rounded-full bg-surface-elevated text-muted text-[0.7rem] tracking-wide uppercase border border-border hover:text-accent hover:border-accent/40 transition-colors"
            >
              {block.title}
            </Link>
          )}
        </motion.div>

        <motion.h1
          variants={fade}
          className="font-display text-3xl md:text-4xl font-semibold text-foreground mb-4 leading-tight"
        >
          {note.title}
        </motion.h1>
        <motion.p variants={fade} className="text-muted text-lg leading-relaxed mb-6">
          {note.summary}
        </motion.p>

        {/* External links */}
        {(links.notion || links.github || links.paper) && (
          <motion.div variants={fade} className="flex flex-wrap gap-x-5 gap-y-2 mb-8">
            {links.notion && (
              <a
                href={links.notion}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-accent text-sm hover:text-accent-hover transition-colors"
              >
                <ExternalLink className="w-4 h-4" /> Open Notion book
              </a>
            )}
            {links.github && (
              <a
                href={links.github}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-accent text-sm hover:text-accent-hover transition-colors"
              >
                <Github className="w-4 h-4" /> GitHub
              </a>
            )}
            {links.paper && (
              <a
                href={links.paper}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-accent text-sm hover:text-accent-hover transition-colors"
              >
                <FileText className="w-4 h-4" /> Paper
              </a>
            )}
          </motion.div>
        )}

        {/* Images */}
        {note.images?.length > 0 && (
          <motion.div
            variants={fade}
            className={`grid gap-2 mb-8 ${note.images.length > 1 ? "sm:grid-cols-2" : "grid-cols-1"}`}
          >
            {note.images.map((img, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setLightbox({ src: img, alt: note.title })}
                className="group overflow-hidden rounded-lg border border-border bg-background cursor-zoom-in"
              >
                <img
                  src={img}
                  alt={`${note.title}, figure ${i + 1}`}
                  loading="lazy"
                  className="w-full h-56 object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                />
              </button>
            ))}
          </motion.div>
        )}

        {/* Body */}
        <motion.div variants={fade}>
          <MarkdownLite source={note.body || ""} />
        </motion.div>

        {/* Tags */}
        {note.tags?.length > 0 && (
          <motion.div variants={fade} className="mt-10 pt-6 border-t border-border">
            <p className="text-muted-foreground text-xs uppercase tracking-wide mb-3">Tags</p>
            <div className="flex flex-wrap gap-2">
              {note.tags.map((t) => (
                <HashTag key={t} tag={t} variant="chip" />
              ))}
            </div>
          </motion.div>
        )}
      </article>

      {/* Related */}
      {related.length > 0 && (
        <div className="max-w-6xl mx-auto mt-16">
          <motion.h2
            variants={fade}
            className="font-display text-2xl font-semibold text-foreground mb-6"
          >
            Related notes
          </motion.h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((n) => (
              <NoteCard key={n.slug} note={n} showBlock />
            ))}
          </div>
        </div>
      )}

      {lightbox && (
        <Lightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />
      )}
    </motion.div>
  );
}
